import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { NavLeaf } from '../nav.model'
import { NavigationService } from '../navigation.service'

/**
 * One page that hosts a group of related pages as tabs.
 *
 * There is a single generic hub rather than one component per section: the tabs
 * are read from the nav config by `hubId`, so a hub and the sidebar always agree
 * on what exists, in what order, and who may see it. Adding a tab is a config
 * edit plus a child route — no new component.
 *
 * Generalizes the hand-rolled shells in tiers-home and ticket-providers-home,
 * and adds the permission filtering neither of those had.
 */
@Component({
  selector: 'app-tab-hub',
  templateUrl: './tab-hub.component.html',
  styleUrls: ['./tab-hub.component.scss'],
})
export class TabHubComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  title = ''
  tabs: NavLeaf[] = []
  selectedIndex = 0

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private nav: NavigationService,
  ) {}

  ngOnInit(): void {
    const data = this.route.snapshot.data
    this.title = data['title'] || ''
    this.tabs = this.nav.groupChildren(data['hubId'])

    this.sync()
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.sync())
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onTabChange(event: { index?: number }): void {
    const tab = this.tabs[event.index ?? -1]
    if (tab) this.router.navigate([tab.route])
  }

  private sync(): void {
    const url = this.router.url.split('?')[0].split('#')[0]

    // Landing on the bare hub path goes to the first tab this admin can open —
    // a fixed default would dead-end anyone without access to that one page.
    const hubPath = this.hubPath()
    if (url === hubPath) {
      if (this.tabs.length) this.router.navigate([this.tabs[0].route], { replaceUrl: true })
      return
    }

    // Segment-boundary match, and the longest one wins. A tab's own module adds
    // segments of its own — `/geo/cities` lands on `/geo/cities/list` — so an
    // exact comparison would leave every tab looking inactive.
    let best = -1
    this.tabs.forEach((tab, index) => {
      const isMatch = url === tab.route || url.startsWith(`${tab.route}/`)
      if (isMatch && (best < 0 || tab.route.length > this.tabs[best].route.length)) {
        best = index
      }
    })
    if (best >= 0) this.selectedIndex = best
  }

  private hubPath(): string {
    return '/' + this.route.snapshot.pathFromRoot.flatMap((r) => r.url.map((s) => s.path)).join('/')
  }
}
